import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import TutorLayout from "./TutorLayout";

function EditUser() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm] = useState({
    displayName: "",
    surname: "",
    email: "",
    role: "student",
  });
  const [message, setMessage] = useState("");

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const fetchedUsers = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(fetchedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter((u) => {
    const fullName = `${u?.displayName || ""} ${u?.surname || ""} ${u?.email || ""}`;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleEditClick = (user) => {
    setEditingUserId(user.id);
    setEditForm({
      displayName: user.displayName || "",
      surname: user.surname || "",
      email: user.email || "",
      role: user.role || "student",
    });
    setMessage("");
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (id) => {
    try {
      const userRef = doc(db, "users", id);
      await updateDoc(userRef, { ...editForm });

      // Update Firebase Authentication via Admin SDK
      const userToUpdate = users.find((u) => u.id === id);
      const uid = userToUpdate.uid || id;

      await fetch(`http://localhost:5001/update-user/${uid}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: editForm.email,
          displayName: editForm.displayName,
        }),
      });

      setMessage("✅ User updated successfully.");
      setEditingUserId(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setMessage("❌ Failed to update user.");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this user?");
    if (!confirmDelete) return;

    try {
      const userToDelete = users.find((u) => u.id === id);
      const uid = userToDelete.uid || id;

      await deleteDoc(doc(db, "users", id));

      await fetch(`http://localhost:5001/delete-user/${uid}`, {
        method: "DELETE",
      });

      setMessage("🗑️ User deleted successfully.");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage("❌ Failed to delete user.");
    }
  };

  return (
    <TutorLayout>
      <div style={styles.container}>
        <h2 style={styles.heading}>✏️ Edit Users</h2>
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.input}
        />

        {message && <p style={styles.message}>{message}</p>}

        {filteredUsers.length === 0 ? (
          <p style={styles.noResults}>No users found.</p>
        ) : (
          <ul style={styles.list}>
            {filteredUsers.map((user) => (
              <li key={user.id} style={styles.userCard}>
                {editingUserId === user.id ? (
                  <>
                    <input
                      type="text"
                      value={editForm.displayName}
                      onChange={(e) => handleEditChange("displayName", e.target.value)}
                      style={styles.editInput}
                    />
                    <input
                      type="text"
                      value={editForm.surname}
                      onChange={(e) => handleEditChange("surname", e.target.value)}
                      style={styles.editInput}
                    />
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => handleEditChange("email", e.target.value)}
                      style={styles.editInput}
                    />
                    <select
                      value={editForm.role}
                      onChange={(e) => handleEditChange("role", e.target.value)}
                      style={styles.editInput}
                    >
                      <option value="student">Student</option>
                      <option value="tutor">Tutor</option>
                    </select>
                    <button onClick={() => handleSave(user.id)} style={styles.saveButton}>
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <p><strong>👤 Name:</strong> {user.displayName} {user.surname}</p>
                    <p><strong>📧 Email:</strong> {user.email}</p>
                    <p><strong>🎓 Role:</strong> {user.role}</p>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button onClick={() => handleEditClick(user)} style={styles.editButton}>
                        Edit
                      </button>
                      <button onClick={() => handleDelete(user.id)} style={styles.deleteButton}>
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </TutorLayout>
  );
}

const styles = {
  container: {
    maxWidth: "700px",
    margin: "2rem auto",
    padding: "2rem",
    backgroundColor: "#fefefe",
    borderRadius: "12px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    fontFamily: "'Poppins', sans-serif",
  },
  heading: {
    fontSize: "1.8rem",
    marginBottom: "1rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    fontSize: "1rem",
    marginBottom: "1.5rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  list: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  userCard: {
    border: "1px solid #ddd",
    padding: "1rem",
    marginBottom: "1.5rem",
    borderRadius: "8px",
    backgroundColor: "#fafafa",
  },
  editInput: {
    width: "100%",
    padding: "0.6rem",
    marginBottom: "0.5rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "1rem",
  },
  editButton: {
    backgroundColor: "#007bff",
    color: "white",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#dc3545",
    color: "white",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  saveButton: {
    backgroundColor: "#28a745",
    color: "white",
    padding: "0.5rem 1rem",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    marginTop: "0.5rem",
  },
  message: {
    marginBottom: "1rem",
    color: "#155724",
    backgroundColor: "#d4edda",
    padding: "0.75rem",
    borderRadius: "6px",
  },
  noResults: {
    textAlign: "center",
    color: "#888",
  },
};

export default EditUser;
